import React, { useRef } from 'react';
import {
  Apple,
  ArrowRight,
  Bell,
  Camera,
  Check,
  CreditCard,
  DoorOpen,
  Download,
  Droplets,
  FileText,
  Folder,
  Key,
  Lock,
  MoreHorizontal,
  PaintBucket,
  Phone,
  Play,
  Send,
  Shield,
  Smartphone,
  Unlock,
  User,
  UserPlus,
  Users,
  Wrench,
  Wind,
  Zap
} from 'lucide-react';
import Footer from './Footer';

const ResidentsPage = ({ showPage }) => {
  const rentRef = useRef(null);
  const electricityRef = useRef(null);
  const maintenanceRef = useRef(null);
  const communityRef = useRef(null);
  const accessRef = useRef(null);
  const documentsRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="page active">
      {/* HERO SECTION */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-blue-50 via-white to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-6">
                <User className="w-3 h-3" /> For Residents
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-neutral-900 mb-6">
                Your home,<br />your way.
              </h1>
              <p className="text-xl text-neutral-600 leading-relaxed mb-8">
                Everything you need to manage your apartment — from paying rent to buying electricity, requesting repairs, and connecting with your community.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://apps.apple.com/ng/app/bilm/id6471915924"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-small text-white bg-neutral-900 hover:bg-neutral-800 shadow-lg transition-all gap-2"
                >
                  <Apple className="w-4 h-4" /> Download for iOS
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.builm&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-small text-neutral-700 bg-white border border-neutral-300 hover:bg-neutral-50 shadow-sm transition-all gap-2"
                >
                  <Play className="w-4 h-4" /> Download for Android
                </a>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div 
                onClick={() => scrollToSection(rentRef)}
                className="bg-white rounded-large shadow-custom p-4 border border-neutral-100 text-center hover:shadow-custom-hover transition-all cursor-pointer"
              >
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Pay Rent</div>
              </div>
              <div 
                onClick={() => scrollToSection(electricityRef)}
                className="bg-amber-50 rounded-large shadow-custom p-4 border border-amber-200 text-center hover:shadow-custom-hover transition-all cursor-pointer relative"
              >
                <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Electricity</div>
              </div>
              <div 
                onClick={() => scrollToSection(maintenanceRef)}
                className="bg-white rounded-large shadow-custom p-4 border border-neutral-100 text-center hover:shadow-custom-hover transition-all cursor-pointer"
              >
                <div className="w-10 h-10 bg-green-100 text-green-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <Wrench className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Maintenance</div>
              </div>
              <div 
                onClick={() => scrollToSection(communityRef)}
                className="bg-white rounded-large shadow-custom p-4 border border-neutral-100 text-center hover:shadow-custom-hover transition-all cursor-pointer"
              >
                <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <Users className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Community</div>
              </div>
              <div 
                onClick={() => scrollToSection(accessRef)}
                className="bg-white rounded-large shadow-custom p-4 border border-neutral-100 text-center hover:shadow-custom-hover transition-all cursor-pointer"
              >
                <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <Key className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Access</div>
              </div>
              <div 
                onClick={() => scrollToSection(documentsRef)}
                className="bg-white rounded-large shadow-custom p-4 border border-neutral-100 text-center hover:shadow-custom-hover transition-all cursor-pointer"
              >
                <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-large flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-neutral-700">Documents</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RENT PAYMENTS SECTION */}
      <section ref={rentRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-6">
                <CreditCard className="w-3 h-3" /> Rent Payments
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Pay rent the way you want</h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                No more bank queues or missed due dates. Bilm makes rent payments instant, secure, and flexible — with multiple payment options and automatic receipts.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Bank transfer, card & USSD</div>
                    <div className="text-neutral-500 text-sm">Pay with Visa, Mastercard, Verve, or direct bank transfer</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Autopay scheduling</div>
                    <div className="text-neutral-500 text-sm">Set a payment date and Bilm handles it every month</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Instant PDF receipts</div>
                    <div className="text-neutral-500 text-sm">Sent to your email and saved in-app automatically</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Due date reminders</div>
                    <div className="text-neutral-500 text-sm">Get notified 7 days, 3 days, and 1 day before rent is due</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Full payment history</div>
                    <div className="text-neutral-500 text-sm">View and download all past rent payments anytime</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
                <div className="bg-primary-600 p-5 text-white">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-heading font-bold">Pay Rent</span>
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Due in 3 days</span>
                  </div>
                  <div className="text-3xl font-extrabold font-heading">₦85,000</div>
                  <div className="text-primary-200 text-sm mt-1">November 2024</div>
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm py-2 border-b border-neutral-100">
                    <span className="text-neutral-500">Base Rent</span>
                    <span className="font-semibold text-neutral-900">₦80,000</span>
                  </div>
                  <div className="flex justify-between text-sm py-2 border-b border-neutral-100">
                    <span className="text-neutral-500">Service Charge</span>
                    <span className="font-semibold text-neutral-900">₦5,000</span>
                  </div>
                  <div className="flex justify-between text-sm py-2 font-bold">
                    <span>Total Due</span>
                    <span>₦85,000</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <button className="py-2.5 text-xs font-semibold rounded-small bg-neutral-900 text-white flex flex-col items-center gap-1">
                      <CreditCard className="w-3 h-3" />Card
                    </button>
                    <button className="py-2.5 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-700 flex flex-col items-center gap-1">
                      <Smartphone className="w-3 h-3" />Transfer
                    </button>
                    <button className="py-2.5 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-700 flex flex-col items-center gap-1">
                      <Phone className="w-3 h-3" />USSD
                    </button>
                  </div>
                  <button className="w-full py-3 bg-primary-600 text-white font-bold rounded-small text-sm hover:bg-primary-700 transition-colors">
                    Pay ₦85,000 Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUY ELECTRICITY SECTION */}
      <section ref={electricityRef} className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 50%, #115e59 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs font-semibold mb-6 backdrop-blur-sm">
                <Zap className="w-3 h-3" /> Buy Electricity
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Buy electricity in 10 seconds, not 45 minutes
              </h2>
              <p className="text-teal-100 leading-relaxed mb-8 text-lg">
                Integrated with major DISCOs across Nigeria. Buy prepaid tokens instantly from the app — no queues, no stress.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">All major DISCOs supported</div>
                    <div className="text-teal-100 text-sm">EKEDC, IKEDC, AEDC, IBEDC, and more</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">Instant token delivery</div>
                    <div className="text-teal-100 text-sm">Token delivered to app and SMS within seconds</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">Low balance alerts</div>
                    <div className="text-teal-100 text-sm">Get notified before you run out — available on eligible DISCOs and meters</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-white/20 text-white rounded-full flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-white text-sm">Bring your own meter</div>
                    <div className="text-teal-100 text-sm">Already have a prepaid meter? Just add it and start buying</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Electricity phone mockup - matches app UI */}
            <div className="flex items-center justify-center">
              <div className="w-[280px] bg-white rounded-2xl shadow-2xl border border-neutral-100 overflow-hidden">
                <div className="px-5 pt-5 pb-3">
                  <div className="font-heading font-bold text-lg text-neutral-900 mb-3">Buy Power</div>
                  <div className="bg-teal-600 rounded-xl p-4 text-white flex justify-between items-start">
                    <div>
                      <div className="text-teal-200 text-[10px] uppercase tracking-wide font-semibold mb-1">Meter Number</div>
                      <div className="font-heading font-bold text-lg">45110012345</div>
                      <div className="text-teal-200 text-xs mt-0.5">Unit 402 - EKEDC</div>
                    </div>
                    <div className="w-9 h-9 bg-teal-500/50 rounded-full flex items-center justify-center">
                      <Zap className="w-4 h-4 text-amber-300" />
                    </div>
                  </div>
                </div>
                <div className="px-5 pb-2">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-neutral-400 mb-2">Enter Amount</div>
                  <div className="border border-neutral-200 rounded-lg px-3 py-2.5 mb-3 flex items-center">
                    <span className="text-neutral-400 text-sm mr-1">N</span>
                    <span className="font-heading font-bold text-neutral-900">1,000</span>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 mb-3">
                    <div className="py-1.5 text-xs font-medium rounded-lg border border-neutral-200 text-neutral-600 text-center">N1k</div>
                    <div className="py-1.5 text-xs font-medium rounded-lg border border-neutral-200 text-neutral-600 text-center">N2k</div>
                    <div className="py-1.5 text-xs font-medium rounded-lg border border-neutral-200 text-neutral-600 text-center">N5k</div>
                    <div className="py-1.5 text-xs font-medium rounded-lg border border-neutral-200 text-neutral-600 text-center">N10k</div>
                  </div>
                </div>
                <div className="px-5 pb-3">
                  <div className="border border-neutral-200 rounded-lg px-3 py-2.5 flex justify-between items-center">
                    <span className="text-sm text-neutral-500">Estimated Units</span>
                    <span className="font-heading font-bold text-teal-600">~18.5 kWh</span>
                  </div>
                </div>
                <div className="px-5 pb-5">
                  <div className="w-full py-3 bg-teal-600 text-white font-semibold rounded-lg text-sm flex items-center justify-center gap-2">
                    Purchase Units
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAINTENANCE SECTION */}
      <section ref={maintenanceRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold mb-6">
                <Wrench className="w-3 h-3" /> Maintenance
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Report issues, track repairs</h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Submit maintenance requests in seconds with photos and descriptions. Track progress in real time and get notified at every step — from submission to completion.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Photo & video attachments</div>
                    <div className="text-neutral-500 text-sm">Show exactly what needs fixing with visual evidence</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Real-time status updates</div>
                    <div className="text-neutral-500 text-sm">Know exactly when a technician is assigned and coming</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Schedule preferred visit time</div>
                    <div className="text-neutral-500 text-sm">Choose a time that works for you</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Rate your experience</div>
                    <div className="text-neutral-500 text-sm">Leave feedback to help improve service quality</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Full request history</div>
                    <div className="text-neutral-500 text-sm">View all past maintenance requests and their outcomes</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
                <div className="bg-green-600 p-5 text-white flex items-center justify-between">
                  <h3 className="font-heading font-bold">New Maintenance Request</h3>
                  <Wrench className="w-5 h-5" />
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Issue Category</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button className="maint-cat selected py-2 px-2 text-xs font-semibold rounded-small border-2 border-green-400 bg-green-50 text-green-700 flex flex-col items-center gap-1">
                        <Droplets className="w-4 h-4" />Plumbing
                      </button>
                      <button className="maint-cat py-2 px-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 flex flex-col items-center gap-1 hover:border-green-300">
                        <Zap className="w-4 h-4" />Electrical
                      </button>
                      <button className="maint-cat py-2 px-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 flex flex-col items-center gap-1 hover:border-green-300">
                        <Wind className="w-4 h-4" />HVAC
                      </button>
                      <button className="maint-cat py-2 px-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 flex flex-col items-center gap-1 hover:border-green-300">
                        <DoorOpen className="w-4 h-4" />Doors
                      </button>
                      <button className="maint-cat py-2 px-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 flex flex-col items-center gap-1 hover:border-green-300">
                        <PaintBucket className="w-4 h-4" />Painting
                      </button>
                      <button className="maint-cat py-2 px-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 flex flex-col items-center gap-1 hover:border-green-300">
                        <MoreHorizontal className="w-4 h-4" />Other
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Description</label>
                    <textarea className="w-full p-3 border border-neutral-200 rounded-small text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-green-400 resize-none" rows="3" placeholder="Describe the issue in detail..."></textarea>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Preferred Visit Time</label>
                    <div className="grid grid-cols-3 gap-2">
                      <button className="visit-time selected py-2 text-xs font-semibold rounded-small border-2 border-green-400 bg-green-50 text-green-700">Morning</button>
                      <button className="visit-time py-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 hover:border-green-300">Afternoon</button>
                      <button className="visit-time py-2 text-xs font-semibold rounded-small border border-neutral-200 text-neutral-600 hover:border-green-300">Evening</button>
                    </div>
                  </div>
                  <div className="border-2 border-dashed border-neutral-200 rounded-small p-3 text-center text-sm text-neutral-400 cursor-pointer hover:border-green-300 hover:bg-green-50 transition-colors">
                    <Camera className="w-5 h-5 mx-auto mb-1" /> Tap to add photos or videos
                  </div>
                  <button className="w-full py-3 bg-green-600 text-white font-bold rounded-small text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" /> Submit Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY SECTION */}
      <section ref={communityRef} className="py-20 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold mb-6">
                <Users className="w-3 h-3" /> Community
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Connect with your building</h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Build a real sense of community. Stay informed about building news, join events, and connect with your neighbors — all in one place.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Building announcements</div>
                    <div className="text-neutral-500 text-sm">Receive instant notifications for important building updates</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Community events</div>
                    <div className="text-neutral-500 text-sm">RSVP to building events and get reminders</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Neighbor messaging</div>
                    <div className="text-neutral-500 text-sm">Private and group messaging with fellow residents</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Community board</div>
                    <div className="text-neutral-500 text-sm">Post updates, ask questions, and share with building</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Package notifications</div>
                    <div className="text-neutral-500 text-sm">Get notified when a package arrives for you</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
                <div className="bg-purple-600 p-4 text-white flex items-center justify-between">
                  <h3 className="font-heading font-bold">Community Board</h3>
                  <span className="text-xs bg-white/20 px-2 py-1 rounded-full">The Lofts</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-large border border-purple-100">
                    <div className="w-8 h-8 bg-purple-200 rounded-full flex items-center justify-center text-purple-700 font-bold text-xs shrink-0">M</div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="text-sm font-semibold text-neutral-900">Management</div>
                        <span className="text-xs text-neutral-400">2 min ago</span>
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">📢 Pool maintenance scheduled for Saturday 8am–12pm.</p>
                      <div className="flex gap-3 mt-2">
                        <button className="text-xs text-purple-600 font-semibold">👍 12</button>
                        <button className="text-xs text-neutral-400 hover:text-purple-600">Reply</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white rounded-large border border-neutral-100">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-xs shrink-0">J</div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="text-sm font-semibold text-neutral-900">James (Unit 301)</div>
                        <span className="text-xs text-neutral-400">1 hr ago</span>
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">Anyone interested in a rooftop BBQ this weekend? 🔥</p>
                      <div className="flex gap-3 mt-2">
                        <button className="text-xs text-purple-600 font-semibold">👍 8</button>
                        <button className="text-xs text-neutral-400 hover:text-purple-600">Reply</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-large border border-amber-100">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 font-bold text-xs shrink-0">📦</div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div className="text-sm font-semibold text-neutral-900">Package Alert</div>
                        <span className="text-xs text-neutral-400">3 hrs ago</span>
                      </div>
                      <p className="text-sm text-neutral-600 mt-1">A package for Unit 402 has arrived at front desk.</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <input type="text" placeholder="Write a post..." className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-small focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder-neutral-400" />
                    <button className="px-3 py-2 bg-purple-600 text-white rounded-small text-sm font-semibold hover:bg-purple-700 transition-colors">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACCESS & SECURITY SECTION */}
      <section ref={accessRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-6">
                <Shield className="w-3 h-3" /> Access & Security
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">Your home, always secure</h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Digital keys, visitor management, and real-time security alerts — all controlled from your phone. Know who enters your building and when.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Digital key access</div>
                    <div className="text-neutral-500 text-sm">Unlock your unit and building entrance from your phone</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Visitor pre-authorization</div>
                    <div className="text-neutral-500 text-sm">Grant access to guests remotely before they arrive</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Entry & exit logs</div>
                    <div className="text-neutral-500 text-sm">Full timestamped log of all building access events</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Security alerts</div>
                    <div className="text-neutral-500 text-sm">Get instant push notifications for unusual access activity</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Temporary access codes</div>
                    <div className="text-neutral-500 text-sm">Generate time-limited codes for service workers or guests</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
                <div className="bg-gradient-to-br from-teal-600 to-cyan-700 p-6 text-white">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-heading font-bold text-lg">Digital Key</span>
                    <Shield className="w-5 h-5" />
                  </div>
                  <div className="text-center">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Unlock className="w-10 h-10 text-white" />
                    </div>
                    <div className="font-heading font-bold text-xl mb-1">Unlocked</div>
                    <div className="text-teal-200 text-sm">Unit 402 • The Lofts</div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-small text-sm transition-colors flex items-center justify-center gap-2">
                    <Lock className="w-4 h-4" /> Lock Unit
                  </button>
                  <div className="grid grid-cols-2 gap-3">
                    <button className="py-2.5 border border-neutral-200 text-neutral-700 text-xs font-semibold rounded-small hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1">
                      <UserPlus className="w-3 h-3" /> Add Visitor
                    </button>
                    <button className="py-2.5 border border-neutral-200 text-neutral-700 text-xs font-semibold rounded-small hover:bg-neutral-50 transition-colors flex items-center justify-center gap-1">
                      <Key className="w-3 h-3" /> Temp Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DOCUMENTS SECTION */}
      <section ref={documentsRef} className="py-20 bg-gradient-to-br from-rose-50 via-white to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-6">
                <FileText className="w-3 h-3" /> Documents
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">All your documents, one place</h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                Access your lease agreement, rent receipts, electricity token history, and all important documents — anytime, anywhere. No more searching through emails.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Lease agreements</div>
                    <div className="text-neutral-500 text-sm">Sign digitally and access your lease anytime</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Rent payment receipts</div>
                    <div className="text-neutral-500 text-sm">Every rent payment receipt, downloadable as PDF</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Electricity token history</div>
                    <div className="text-neutral-500 text-sm">All past tokens with amounts, dates, and kWh units</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Maintenance records</div>
                    <div className="text-neutral-500 text-sm">Full history of all maintenance requests and resolutions</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900 text-sm">Building notices</div>
                    <div className="text-neutral-500 text-sm">Archived announcements and official building notices</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-large shadow-custom-hover border border-neutral-100 overflow-hidden">
              <div className="bg-rose-600 p-5 text-white flex items-center justify-between">
                <h3 className="font-heading font-bold">My Documents</h3>
                <Folder className="w-5 h-5" />
              </div>
              <div className="p-5 space-y-2">
                <div className="flex items-center gap-3 p-3 border border-neutral-100 rounded-large hover:bg-neutral-50 cursor-pointer group transition-colors">
                  <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-large flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-neutral-900">Lease Agreement 2024</div>
                    <div className="text-xs text-neutral-400">Signed Jan 15, 2024 • PDF</div>
                  </div>
                  <Download className="w-4 h-4 text-neutral-300 group-hover:text-rose-500 transition-colors shrink-0" />
                </div>
                <div className="p-3 border border-amber-100 rounded-large bg-amber-50/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 bg-amber-100 text-amber-600 rounded-large flex items-center justify-center shrink-0">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-neutral-900">Electricity Tokens</div>
                      <div className="text-xs text-neutral-400">24 purchases</div>
                    </div>
                    <button className="text-xs text-amber-600 font-semibold hover:text-amber-700">View All</button>
                  </div>
                  <div className="space-y-1.5 pl-12">
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-600">Nov 28 • 4521 8834...</span>
                      <span className="font-semibold text-amber-700">₦2,000 • 37 kWh</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-600">Nov 14 • 7823 4412...</span>
                      <span className="font-semibold text-amber-700">₦1,000 • 18.5 kWh</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-600">Nov 2 • 3310 9987...</span>
                      <span className="font-semibold text-amber-700">₦5,000 • 92.5 kWh</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 border border-neutral-100 rounded-large hover:bg-neutral-50 cursor-pointer group transition-colors">
                  <div className="w-9 h-9 bg-green-100 text-green-600 rounded-large flex items-center justify-center shrink-0">
                    <Wrench className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-neutral-900">Maintenance Records</div>
                    <div className="text-xs text-neutral-400">7 requests • 6 resolved</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-rose-500 transition-colors shrink-0" />
                </div>
                <div className="flex items-center gap-3 p-3 border border-neutral-100 rounded-large hover:bg-neutral-50 cursor-pointer group transition-colors">
                  <div className="w-9 h-9 bg-purple-100 text-purple-600 rounded-large flex items-center justify-center shrink-0">
                    <Bell className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-neutral-900">Building Notices</div>
                    <div className="text-xs text-neutral-400">14 archived notices</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-rose-500 transition-colors shrink-0" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A2A6C 0%, #2D52B8 50%, #4F8EF7 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">Your home, in your pocket</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">Download Bilm and start managing your apartment — pay rent, buy electricity, and request repairs in seconds.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://apps.apple.com/ng/app/bilm/id6471915924"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-small text-primary-900 bg-white hover:bg-blue-50 shadow-lg transition-all gap-2"
            >
              <Apple className="w-5 h-5" /> App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.builm&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-7 py-3.5 border border-white/30 text-base font-semibold rounded-small text-white hover:bg-white/10 transition-all gap-2 backdrop-blur-sm"
            >
              <Play className="w-5 h-5" /> Google Play
            </a>
          </div>
        </div>
      </section>

      <Footer showPage={showPage} />
    </div>
  );
};

export default ResidentsPage;
